// 📄 components/shared/XmlDropzone.tsx
// Carga de un archivo XML: drag & drop + click-to-browse + preview
// colapsable con resaltado básico. Extraído de ValidadorEstructura.tsx
// (donde vivía como "CargaXML", local al archivo) para reutilizarlo en
// CargarPlantillaModal.tsx (Configuración > Plantillas XML) sin duplicar
// la lógica de lectura/validación del archivo.
import React, { useRef, useState } from "react";
import { ChevronDown, ChevronUp, UploadCloud } from "lucide-react";
import { parseXmlString } from "../../utils/xmlStructureUtils.ts";

export type XmlDropzoneAccent = "purple" | "amber" | "indigo";

interface XmlDropzoneProps {
  accent: XmlDropzoneAccent;
  nombreArchivo: string;
  contenidoArchivo: string;
  onArchivoCargado: (nombre: string, contenido: string) => void;
  onError: (mensaje: string) => void;
}

const ACCENTOS: Record<XmlDropzoneAccent, { border: string; text: string; bg: string }> = {
  purple: { border: "border-purple-400", text: "text-purple-400", bg: "bg-purple-500/10" },
  amber: { border: "border-amber-400", text: "text-amber-400", bg: "bg-amber-500/10" },
  indigo: { border: "border-indigo-400", text: "text-indigo-400", bg: "bg-indigo-500/10" }
};

// Primeras líneas del XML cargado, con un resaltado bien básico (regex, sin
// librería externa) — alcanza para que el usuario confirme de un vistazo
// que subió el archivo correcto antes de gastar un intento de validación.
function resaltarXML(texto: string, maxLineas: number): string {
  const lineas = texto.split("\n").slice(0, maxLineas);
  return lineas
    .map((linea) => {
      const escapada = linea
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return escapada
        .replace(/([a-zA-Z0-9:_-]+)(=)(&quot;|")([^"]*?)(&quot;|")/g, '<span class="text-amber-300">$1</span>$2<span class="text-emerald-300">$3$4$5</span>')
        .replace(/(&lt;\/?[a-zA-Z0-9:_-]+|&gt;)/g, '<span class="text-sky-400">$1</span>');
    })
    .join("\n");
}

export default function XmlDropzone({ accent, nombreArchivo, contenidoArchivo, onArchivoCargado, onError }: XmlDropzoneProps) {
  const [arrastrando, setArrastrando] = useState(false);
  const [previewAbierto, setPreviewAbierto] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const colores = ACCENTOS[accent];

  const procesarArchivo = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const texto = reader.result as string;

      // Valida que sea XML bien formado en el momento de subirlo — un
      // archivo roto nunca debe guardarse como plantilla ni usarse para
      // validar, siempre con el mismo error sin importar qué se suba después.
      try {
        parseXmlString(texto);
      } catch (err: any) {
        onError(err.message || "El archivo subido no es un XML válido.");
        return;
      }

      setPreviewAbierto(false);
      onArchivoCargado(file.name, texto);
    };
    reader.readAsText(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) procesarArchivo(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setArrastrando(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xml")) {
      onError("Solo se aceptan archivos .xml.");
      return;
    }
    procesarArchivo(file);
  };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          arrastrando ? `${colores.border} ${colores.bg}` : "border-white/15 hover:border-white/30"
        }`}
      >
        <UploadCloud className={`w-8 h-8 ${arrastrando ? colores.text : "text-gray-500"} transition-colors`} />
        <p className="text-sm text-gray-300">
          Arrastra tu archivo XML aquí o <span className={`${colores.text} font-semibold`}>haz click para seleccionar</span>
        </p>
        <input
          ref={inputRef}
          type="file"
          accept=".xml"
          onChange={handleInputChange}
          className="hidden"
        />
      </div>

      {nombreArchivo && (
        <div className="mt-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs text-gray-400">{nombreArchivo}</span>
            <button
              type="button"
              onClick={() => setPreviewAbierto((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
            >
              {previewAbierto ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {previewAbierto ? "Ocultar" : "Ver"} contenido
            </button>
          </div>

          {previewAbierto && (
            <pre className="mt-2 max-h-64 overflow-auto bg-black/40 border border-white/10 rounded-xl p-4 text-[11px] font-mono leading-relaxed text-gray-300 whitespace-pre-wrap">
              <code dangerouslySetInnerHTML={{ __html: resaltarXML(contenidoArchivo, 40) }} />
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
